import React from 'react'
import PropTypes from 'prop-types'
import { Badge, Card, Grid, IconButton } from '@mui/material'
import { StyledFieldSet, StyledLegend } from '../../../../components/StyledElements'
import i18n from '../../../../simple-react-i18n'
import Input from '../../../../components/forms/Input'
import Select from '../../../../components/forms/Select'
import { getSetting, isTrue } from '../../../../utils/SettingUtils'
import { TIMEZONE_BROWSER, TIMEZONE_UTC } from '../../../../utils/DateUtil'
import Checkbox from '../../../../components/forms/Checkbox'
import SettingSms from '../smsConfig/SetttingSms'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { APPLICATION_TIMEZONE, NAME_LOGIN_PICTURE, NAME_MENU_PICTURE } from '../../../constants/AdministrationConstants'
import StationAction from '../../../../station/actions/StationAction'
import SieauParameterDto from '../../../dto/SieauParameterDto'
import ClearIcon from '@mui/icons-material/Clear'
import { darkBlue, mainBlue } from '../../../../utils/constants/ColorTheme'
import DtoFile from '../../../../components/file/dto/DtoFile'

const ApplicationParametersPanel = ({
    applicationSettings = [],
    onChangeSettings = () => {},
    disabled = false,
    files= [],
    onChangeFiles = () => {},
}) => {
    const {
        menuPicture,
        loginPicture,
    } = useSelector(store => ({
        menuPicture: store.AdministrationReducer.menuPicture,
        loginPicture: store.AdministrationReducer.loginPicture,
    }), shallowEqual)

    const dispatch = useDispatch()

    const timezones = [
        { code: TIMEZONE_BROWSER, label: i18n.browserTimezone },
        { code: TIMEZONE_UTC, label: i18n.utcTimezone },
    ]

    const onUploadFile = (event, fileType) => {
        // Read File
        if (event.target.files && event.target.files[0]) {
            let reader = new FileReader()
            const file = event.target.files[0]
            reader.onload = (e) => {
                const newfiles = [
                    ...files.filter(f => f.stationType !== fileType),
                    { name: file.name, content: e.target.result, stationType: fileType },
                ]
                onChangeFiles(newfiles)
            }
            reader.readAsDataURL(event.target.files[0])
        }
    }

    return (
        <Card sx={{ padding: '0.5rem', borderRadius: '0 0 5px 5px' }}>
            <StyledFieldSet>
                <StyledLegend>{i18n.application}</StyledLegend>
                <Grid container spacing={1} sx={{ paddingTop: '0.75rem' }}>
                    <Grid size={4}>
                        <Input
                            title={ i18n.applicationTitle }
                            value={ getSetting(applicationSettings, 'applicationTitle') }
                            onChange={ (v) => onChangeSettings('applicationTitle', v) }
                            { ...disabled }
                        />
                    </Grid>
                    <Grid size={4}>
                        <Input
                            title={ i18n.applicationName }
                            value={ getSetting(applicationSettings, 'applicationName') }
                            onChange={ (v) => onChangeSettings('applicationName', v) }
                            { ...disabled }
                        />
                    </Grid>
                    <Grid size={2}>
                        <Input
                            title={ i18n.homePage }
                            value={ getSetting(applicationSettings, 'PageApresConnexion') }
                            onChange={ v => onChangeSettings('PageApresConnexion', v) }
                            { ...disabled }
                        />
                    </Grid>
                    <Grid size={2} container alignItems='center'>
                        <Checkbox
                            label={i18n.envRecette}
                            checked={ isTrue(getSetting(applicationSettings, 'envRecette')) }
                            { ...disabled }
                            onChange={(e) => onChangeSettings('envRecette', e ? '1' : '0')}
                        />
                    </Grid>
                    <Grid size={4}>
                        <Input
                            title={ i18n.applicationFavicon }
                            value={ getSetting(applicationSettings, 'applicationFavicon') }
                            onChange={ (v) => onChangeSettings('applicationFavicon', v) }
                            { ...disabled }
                        />
                    </Grid>
                    <Grid size={4}>
                        <Input
                            title={ i18n.applicationLogoConnexion }
                            value={ getSetting(applicationSettings, 'applicationLogoConnexion') }
                            onChange={ (v) => onChangeSettings('applicationLogoConnexion', v) }
                            { ...disabled }
                        />
                    </Grid>
                    <Grid size={4}>
                        <Input
                            title={ i18n.twitterAccount }
                            value={ `@${getSetting(applicationSettings, 'TwitterUser') || ''}` }
                            onChange={ v => onChangeSettings('TwitterUser', v.substr(1)) }
                            { ...disabled }
                        />
                    </Grid>
                    <Grid size={4}>
                        <Select
                            label={ i18n.timezone }
                            value={ getSetting(applicationSettings, APPLICATION_TIMEZONE) ?? TIMEZONE_BROWSER }
                            options={ timezones }
                            onChange={ v => onChangeSettings(APPLICATION_TIMEZONE, v ?? TIMEZONE_BROWSER) }
                            noNullValue
                            { ...disabled }
                        />
                    </Grid>
                </Grid>
            </StyledFieldSet>
            <StyledFieldSet>
                <StyledLegend>{i18n.meteoNappes}</StyledLegend>
                <Grid container spacing={1} sx={{ paddingTop: '0.75rem' }}>
                    <Grid size={6}>
                        <Input
                            title={ 'Email Météo Nappes' }
                            value={ getSetting(applicationSettings, 'MeteoNappesEmail') }
                            onChange={ v => onChangeSettings('MeteoNappesEmail', v) }
                            { ...disabled }
                        />
                    </Grid>
                    <Grid size={6}>
                        <Input
                            title={ `${i18n.password} Météo Nappes` }
                            value={ getSetting(applicationSettings, 'MeteoNappesPassword') }
                            onChange={ v => onChangeSettings('MeteoNappesPassword', v) }
                            { ...disabled }
                            passwordType
                        />
                    </Grid>
                </Grid>
            </StyledFieldSet>
            <StyledFieldSet>
                <StyledLegend>{i18n.smsMail}</StyledLegend>
                <SettingSms
                    change={onChangeSettings}
                    applicationSettings={applicationSettings}
                    disabled={disabled}
                />
            </StyledFieldSet>
            <StyledFieldSet>
                <StyledLegend>{i18n.others}</StyledLegend>
                <Grid
                    container
                    spacing={1}
                    sx={{
                        paddingTop: '0.75rem',
                        '& img': {
                            width: '100%',
                            height: '150px',
                            objectFit: 'cover',
                        },
                    }}
                >
                    <Grid size={12}>
                        <Input
                            title={i18n.googleCalendarId}
                            value={getSetting(applicationSettings, 'GoogleCalendarID')}
                            onChange={(v) => onChangeSettings('GoogleCalendarID', v)}
                            {...disabled}
                        />
                    </Grid>
                    <Grid size={6}>
                        <Input title={ i18n.version } value={ process.env.REACT_APP_VERSION } disabled/>
                    </Grid>
                    <Grid size={6}>
                        <Input title={ i18n.lastCommit } value={ process.env.REACT_APP_COMMIT } disabled/>
                    </Grid>
                    <Grid size={6}>
                        <label style={{ fontSize: '1rem', fontWeight: 'bold', marginLeft: '1.2rem' }}>
                            {i18n.menuPicture}
                        </label>
                    </Grid>
                    <Grid size={6}>
                        <label style={{ fontSize: '1rem', fontWeight: 'bold', marginLeft: '1.2rem' }}>
                            {i18n.loginPicture}
                        </label>
                    </Grid>
                    <Grid size={6}>
                        {(!disabled.disabled && menuPicture.url) ? (
                            <Badge
                                overlap='circular'
                                badgeContent={
                                    <IconButton
                                        sx={{
                                            backgroundColor: mainBlue,
                                            color: 'white',
                                            '&:hover': {
                                                backgroundColor: darkBlue,
                                            },
                                        }}
                                        onClick={() =>
                                            dispatch(
                                                StationAction.deleteFile({
                                                    name: menuPicture.name,
                                                    fileType: 'public',
                                                }),
                                            )
                                        }
                                    >
                                        <ClearIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                }
                            >
                                <img src={menuPicture.url || '../pictures/background.jpg'} alt={i18n.menuPicture} />
                            </Badge>) : <img src={menuPicture.url || '../pictures/background.jpg'} alt={i18n.menuPicture} />
                        }
                    </Grid>
                    <Grid size={6}>
                        {loginPicture.url && (
                            <>
                                {!disabled.disabled ? (
                                    <Badge
                                        overlap='circular'
                                        badgeContent={
                                            <IconButton
                                                sx={{
                                                    backgroundColor: mainBlue,
                                                    color: 'white',
                                                    '&:hover': {
                                                        backgroundColor: darkBlue,
                                                    },
                                                }}
                                                onClick={() =>
                                                    dispatch(
                                                        StationAction.deleteFile({
                                                            name: menuPicture.name,
                                                            fileType: 'public',
                                                        }),
                                                    )
                                                }
                                            >
                                                <ClearIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        }
                                    >
                                        <img src={loginPicture.url} alt={i18n.loginPicture} />
                                    </Badge>) : <img src={loginPicture.url} alt={i18n.loginPicture} /> }
                            </>
                        )}
                    </Grid>
                </Grid>
                {!disabled.disabled && (
                    <Grid container spacing={1} justifyContent='center'>
                        <Grid size={6}>
                            <div className='file-field input-field'>
                                <div className='btn'>
                                    <span>{ i18n.changeMenuPicture }</span>
                                    <input
                                        accept='image/*'
                                        id='uploadFile'
                                        type='file'
                                        multiple
                                        onChange={ event => onUploadFile(event, NAME_MENU_PICTURE) }
                                    />
                                </div>
                                <div className='file-path-wrapper'>
                                    <input className='file-path validate' type='text' />
                                </div>
                            </div>
                        </Grid>
                        <Grid size={6}>
                            <div className='file-field input-field'>
                                <div className='btn'>
                                    <span>{ i18n.changeLoginPicture }</span>
                                    <input
                                        accept='image/*'
                                        id='uploadFile'
                                        type='file'
                                        multiple
                                        onChange={ event => onUploadFile(event, NAME_LOGIN_PICTURE)}
                                    />
                                </div>
                                <div className='file-path-wrapper'>
                                    <input className='file-path validate' type='text' />
                                </div>
                            </div>
                        </Grid>
                        <h6>{i18n.messageChangeMenuPicture}</h6>
                    </Grid>
                )}
            </StyledFieldSet>
        </Card>
    )
}

ApplicationParametersPanel.propTypes = {
    applicationSettings: PropTypes.arrayOf(PropTypes.instanceOf(SieauParameterDto)),
    onChangeSettings: PropTypes.func,
    disabled: PropTypes.shape({
        active: PropTypes.bool,
        disabled: PropTypes.bool,
    }),
    files: PropTypes.arrayOf(PropTypes.instanceOf(DtoFile)),
    onChangeFiles: PropTypes.func,
}

export default ApplicationParametersPanel